import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import defaultImage from "../../icons/default.jpg";
import "./employeeDashboard.css";

const ROLE_LABELS = {
  "9": "Personal Trainer",
  "10": "Nutrition",
  "11": "Fitness Manager",
  "12": "HR",
};

const TODAY = new Date();
const TODAY_STRING = TODAY.toISOString().split("T")[0];
const MONTH_START_STRING = new Date(TODAY.getFullYear(), TODAY.getMonth(), 1)
  .toISOString()
  .split("T")[0];
const CURRENT_MONTH = `${TODAY.getMonth() + 1}`.padStart(2, "0");
const CURRENT_YEAR = `${TODAY.getFullYear()}`;

const EMPTY_PROFILE_FORM = {
  cnic: "",
  email: "",
  phone: "",
  address: "",
  password: "",
};

const EMPTY_DUTY_FORM = {
  staff_timing_id: "",
  day: "",
  requested_start_time: "",
  requested_end_time: "",
  reason: "",
};

const EMPTY_LEAVE_FORM = {
  type: "Annual",
  leave_status: "Leave",
  category: "Full",
  from: "",
  to: "",
  reason: "",
};

const EMPTY_ENTRY_FORM = {
  entry_type: "Qualification",
  title: "",
  organization: "",
  location: "",
  start_date: "",
  end_date: "",
  description: "",
};

const EMPTY_DOCUMENT_FORM = {
  document_category: "CNIC",
  document_type: "Document",
  issue_date: TODAY_STRING,
  subject: "",
  description: "",
  document_code: "",
};

const EMPTY_ATTENDANCE_FILTERS = {
  start_date: MONTH_START_STRING,
  end_date: TODAY_STRING,
};

const extractItems = (response) => {
  const payload = response?.data?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

const safeModuleItems = async (request) => {
  try {
    return extractItems(await request());
  } catch (error) {
    if ([404, 422, 500].includes(error?.response?.status)) return [];
    throw error;
  }
};

const safeDashboardItems = async (request) => {
  try {
    return extractItems(await request());
  } catch (error) {
    if ([403, 404, 422, 500].includes(error?.response?.status)) return [];
    throw error;
  }
};

const normalizeDutyHourRows = (rows = []) => {
  if (!Array.isArray(rows)) return [];

  if (rows.length && Array.isArray(rows[0]?.dutyHours)) {
    return rows[0].dutyHours;
  }

  return rows.filter((row) => row?.day && (row?.start_time || row?.end_time));
};

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const formatDate = (value) => {
  if (!hasValue(value)) return "N/A";

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!hasValue(value)) return "N/A";

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const badgeClass = (value) => `status-badge ${String(value || "pending").toLowerCase()}`;

const toLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatTimeRange = (start, end) => {
  if (!hasValue(start) && !hasValue(end)) return "Not assigned";
  return `${start || "--"} - ${end || "--"}`;
};

const formatAttendanceClock = (value) => {
  if (!value) return "N/A";

  const normalized = String(value).trim();
  if (!normalized) return "N/A";

  const date = new Date(`1970-01-01T${normalized}`);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  return normalized;
};

const getAttendanceDutyHours = (row) => {
  if (row?.duty_hours) return row.duty_hours;
  if (row?.start_time || row?.end_time) return formatTimeRange(row.start_time, row.end_time);
  return "N/A";
};

const isAttendanceLate = (row) =>
  Number(row?.is_late || 0) === 1 || String(row?.attendance_status || "").toLowerCase() === "late";

const getAttendanceStatus = (row) => {
  if (isAttendanceLate(row)) return "Late";
  return row?.attendance_status || "N/A";
};

const getDisplayName = (profile) => {
  if (!profile) return "Employee Dashboard";

  const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
  return fullName || profile.name || "Employee Dashboard";
};

const canEmployeeEditDocument = (documentRow) =>
  (documentRow?.approval_status || "Pending") !== "Approved";

const announcementPriorityClass = (value) =>
  `employee-announcement-priority ${String(value || "medium").toLowerCase()}`;

const EmptyState = ({ title, text }) => (
  <div className="employee-empty-state">
    <strong>{title}</strong>
    <span>{text}</span>
  </div>
);

const DetailGrid = ({ items }) => (
  <div className="employee-detail-grid">
    {items.map((item) => (
      <div className="employee-detail-item" key={item.label}>
        <span>{item.label}</span>
        <strong>{hasValue(item.value) ? item.value : "N/A"}</strong>
      </div>
    ))}
  </div>
);

const SummaryCard = ({ label, value, meta }) => (
  <div className="employee-summary-card">
    <span>{label}</span>
    <strong>{value}</strong>
    <small>{meta}</small>
  </div>
);

const SectionTitle = ({ eyebrow, title, description, action }) => (
  <div className="employee-panel-header">
    <div>
      {eyebrow ? <span className="employee-section-label">{eyebrow}</span> : null}
      <h5 className="employee-panel-title">{title}</h5>
      {description ? <p className="employee-panel-copy">{description}</p> : null}
    </div>
    {action || null}
  </div>
);

export default function EmployeeDashboard() {
  const { state } = useContext(NoteState);
  const { branch_id, UId, role } = state;
  const isReviewer = role === "12" || role === "1";
  const [activeTab, setActiveTab] = useState(isReviewer ? "approvals" : "profile");
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [profile, setProfile] = useState(null);
  const [dutyHours, setDutyHours] = useState([]);
  const [salaryRows, setSalaryRows] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [leaveQuota, setLeaveQuota] = useState([]);
  const [leaveApplications, setLeaveApplications] = useState([]);
  const [entries, setEntries] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [dutyRequests, setDutyRequests] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceFilters, setAttendanceFilters] = useState(EMPTY_ATTENDANCE_FILTERS);
  const [pendingDutyApprovals, setPendingDutyApprovals] = useState([]);
  const [pendingDocumentApprovals, setPendingDocumentApprovals] = useState([]);
  const [profileForm, setProfileForm] = useState(EMPTY_PROFILE_FORM);
  const [profileFile, setProfileFile] = useState(null);
  const [dutyForm, setDutyForm] = useState(EMPTY_DUTY_FORM);
  const [leaveForm, setLeaveForm] = useState(EMPTY_LEAVE_FORM);
  const [entryForm, setEntryForm] = useState(EMPTY_ENTRY_FORM);
  const [documentForm, setDocumentForm] = useState(EMPTY_DOCUMENT_FORM);
  const [documentFile, setDocumentFile] = useState(null);
  const [dutyEditId, setDutyEditId] = useState(null);
  const [entryEditId, setEntryEditId] = useState(null);
  const [documentEditId, setDocumentEditId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState({});
  const activeBranchId = branch_id || profile?.branch_id || "";

  const tabs = useMemo(() => {
    const defaultTabs = ["profile", "attendance", "duty", "salary", "leave", "background", "documents"];
    return isReviewer ? ["approvals", ...defaultTabs] : defaultTabs;
  }, [isReviewer]);

  const latestSalary = useMemo(() => salaryRows[0] || {}, [salaryRows]);
  const deductionAmount = latestSalary?.detections || latestSalary?.deductions || 0;
  const approvedDocumentsCount = useMemo(
    () => documents.filter((row) => row.approval_status === "Approved").length,
    [documents]
  );
  const pendingRequestCount = useMemo(
    () => dutyRequests.filter((row) => row.approval_status === "Pending").length,
    [dutyRequests]
  );
  const pendingApprovalsCount = pendingDutyApprovals.length + pendingDocumentApprovals.length;
  const leaveBalance = useMemo(
    () =>
      leaveQuota.reduce((total, row) => {
        const allowed = Number(row.number_of_leaves || 0);
        const taken = Number(row.leaves_taken || 0);
        return total + Math.max(allowed - taken, 0);
      }, 0),
    [leaveQuota]
  );
  const attendanceSummary = useMemo(
    () => ({
      total: attendanceRows.length,
      present: attendanceRows.filter((row) => String(row?.attendance_status || "").toLowerCase() === "present").length,
      absent: attendanceRows.filter((row) => String(row?.attendance_status || "").toLowerCase() === "absent").length,
      late: attendanceRows.filter((row) => isAttendanceLate(row)).length,
    }),
    [attendanceRows]
  );

  const summaryCards = useMemo(
    () => [
      {
        label: "Current Salary",
        value: formatCurrency(latestSalary.salary || profile?.salary || 0),
        meta: latestSalary.salary ? "Latest payroll snapshot" : "From staff profile",
      },
      {
        label: isReviewer ? "Needs Review" : "Pending Requests",
        value: `${isReviewer ? pendingApprovalsCount : pendingRequestCount}`,
        meta: isReviewer ? "Duty-hour and document approvals" : "Duty-hour requests awaiting approval",
      },
      {
        label: "Duty Slots",
        value: `${dutyHours.length}`,
        meta: dutyHours.length === 1 ? "Active work day" : "Active work days",
      },
      {
        label: "Leave Balance",
        value: `${leaveBalance}`,
        meta: "Remaining leave days",
      },
      {
        label: "Approved Documents",
        value: `${approvedDocumentsCount}`,
        meta: "Verified staff documents",
      },
    ],
    [approvedDocumentsCount, dutyHours.length, isReviewer, latestSalary.salary, leaveBalance, pendingApprovalsCount, pendingRequestCount, profile?.salary]
  );

  const groupedEntries = useMemo(
    () => ({
      Qualification: entries.filter((row) => row.entry_type === "Qualification"),
      Experience: entries.filter((row) => row.entry_type === "Experience"),
      Education: entries.filter((row) => row.entry_type === "Education"),
    }),
    [entries]
  );

  const loadAttendance = useCallback(
    async (resolvedBranchId, filters = EMPTY_ATTENDANCE_FILTERS) => {
      if (!resolvedBranchId || !UId) {
        setAttendanceRows([]);
        setTodayAttendance(null);
        return;
      }

      const requestFilters = {
        sdate: filters.start_date,
        edate: filters.end_date,
        start_time: "",
        end_time: "",
        gender: "",
      };

      const todayFilters = {
        sdate: TODAY_STRING,
        edate: TODAY_STRING,
        start_time: "",
        end_time: "",
        gender: "",
      };

      const [rangeRows, todayRows] = await Promise.all([
        safeDashboardItems(() =>
          UserServices.getEmployeeAttendence(
            resolvedBranchId,
            requestFilters,
            UId,
            1000,
            1
          )
        ),
        safeDashboardItems(() =>
          UserServices.getEmployeeAttendence(resolvedBranchId, todayFilters, UId, 100, 1)
        ),
      ]);

      const sortedRows = [...rangeRows].sort(
        (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
      );
      const todayRow =
        todayRows.find((row) => String(row.date || "").startsWith(TODAY_STRING)) || todayRows[0] || null;

      setAttendanceRows(sortedRows);
      setTodayAttendance(todayRow);
    },
    [UId]
  );

  const profileSections = useMemo(() => {
    const personal = [
      { label: "Employee ID", value: profile?.uid },
      { label: "First Name", value: profile?.first_name },
      { label: "Last Name", value: profile?.last_name },
      { label: "Father Name", value: profile?.father_name },
      { label: "Gender", value: profile?.gender },
      { label: "Date of Birth", value: formatDate(profile?.dob || profile?.date_of_birth) },
      { label: "CNIC", value: profile?.cnic },
      { label: "Personal Email", value: profile?.email },
      { label: "Official Email", value: profile?.official_email },
      { label: "Phone", value: profile?.phone },
      { label: "Address", value: profile?.address },
    ];

    const employment = [
      { label: "Role", value: ROLE_LABELS[role] || "Employee" },
      { label: "Department", value: profile?.department || profile?.department_name },
      { label: "Designation", value: profile?.designation || profile?.designation_name },
      { label: "Branch", value: profile?.branch_name || profile?.branch },
      { label: "Joining Date", value: formatDate(profile?.joining || profile?.joining_date) },
      {
        label: "Salary",
        value: hasValue(profile?.salary) ? formatCurrency(profile?.salary) : "N/A",
      },
    ];

    const editable = [
      { label: "CNIC", value: profile?.cnic },
      { label: "Personal Email", value: profile?.email },
      { label: "Phone", value: profile?.phone },
      { label: "Address", value: profile?.address },
    ];

    const knownKeys = new Set([
      "id",
      "uid",
      "name",
      "first_name",
      "last_name",
      "father_name",
      "gender",
      "dob",
      "date_of_birth",
      "cnic",
      "email",
      "official_email",
      "phone",
      "address",
      "role",
      "department",
      "department_name",
      "designation",
      "designation_name",
      "branch",
      "branch_name",
      "branch_id",
      "department_id",
      "designation_id",
      "joining",
      "joining_date",
      "salary",
      "commission",
      "management",
      "is_gx_trainer",
      "probation_duration",
      "image",
      "image_url",
      "created_at",
      "updated_at",
      "status",
    ]);

    const additional = Object.entries(profile || {})
      .filter(([key, value]) => !knownKeys.has(key) && hasValue(value) && typeof value !== "object")
      .map(([key, value]) => ({
        label: toLabel(key),
        value: String(value),
      }));

    return { personal, employment, editable, additional };
  }, [profile, role]);

  const loadDashboard = useCallback(async () => {
    if (!UId) return;

    setLoading(true);

    try {
      const profileRows = await safeDashboardItems(() => UserServices.getStaffProfileInfo(UId));
      const currentProfile = profileRows[0] || null;
      const resolvedBranchId = branch_id || currentProfile?.branch_id || "";

      const [
        dutyHourRows,
        quotaRows,
        leaveRows,
        salaryData,
        promotionRows,
        entryRows,
        documentRows,
        dutyRequestRows,
        announcementRows,
        hrDutyRows,
        hrDocumentRows,
      ] = await Promise.all([
        resolvedBranchId
          ? safeDashboardItems(() =>
              UserServices.getEmployeeDutyHours({
                branch_id: resolvedBranchId,
                user_id: UId,
                limit: 999999,
              })
            )
          : Promise.resolve([]),
        resolvedBranchId
          ? safeDashboardItems(() => UserServices.getProfileLeaveQuota(UId, resolvedBranchId, 100, 1))
          : Promise.resolve([]),
        resolvedBranchId
          ? safeDashboardItems(() =>
              UserServices.getProfileLeaveApplication(resolvedBranchId, UId, 100, 1)
            )
          : Promise.resolve([]),
        resolvedBranchId
          ? safeDashboardItems(() =>
              UserServices.getLatestStaffSalarySlip(
                resolvedBranchId,
                CURRENT_MONTH,
                CURRENT_YEAR,
                UId
              )
            )
          : Promise.resolve([]),
        resolvedBranchId
          ? safeDashboardItems(() => UserServices.getFilteredPromotions(UId, resolvedBranchId))
          : Promise.resolve([]),
        resolvedBranchId
          ? safeModuleItems(() =>
              UserServices.getEmployeeProfileEntries({ branch_id: resolvedBranchId, user_id: UId })
            )
          : Promise.resolve([]),
        resolvedBranchId
          ? safeModuleItems(() =>
              UserServices.getEmployeeDocuments({ branch_id: resolvedBranchId, user_id: UId })
            )
          : Promise.resolve([]),
        resolvedBranchId
          ? safeModuleItems(() =>
              UserServices.getEmployeeDutyHourRequests({ branch_id: resolvedBranchId, user_id: UId })
            )
          : Promise.resolve([]),
        safeModuleItems(() =>
          UserServices.getAnnouncements({
            branch_id: resolvedBranchId,
            active_only: true,
            status: "1",
            limit: 20,
          })
        ),
        isReviewer && resolvedBranchId
          ? safeModuleItems(() =>
              UserServices.getEmployeeDutyHourRequests({
                branch_id: resolvedBranchId,
                approval_status: "Pending",
              })
            )
          : Promise.resolve([]),
        isReviewer && resolvedBranchId
          ? safeModuleItems(() =>
              UserServices.getEmployeeDocuments({
                branch_id: resolvedBranchId,
                approval_status: "Pending",
              })
            )
          : Promise.resolve([]),
      ]);

      setProfile(currentProfile);
      setDutyHours(normalizeDutyHourRows(dutyHourRows));
      setLeaveQuota(quotaRows);
      setLeaveApplications(leaveRows);
      setSalaryRows(salaryData);
      setPromotions(promotionRows);
      setEntries(entryRows);
      setDocuments(documentRows);
      setDutyRequests(dutyRequestRows);
      setAnnouncements(announcementRows);
      setPendingDutyApprovals(hrDutyRows);
      setPendingDocumentApprovals(hrDocumentRows);
      await loadAttendance(resolvedBranchId, EMPTY_ATTENDANCE_FILTERS);

      if (currentProfile) {
        setProfileForm({
          cnic: currentProfile.cnic || "",
          email: currentProfile.email || "",
          phone: currentProfile.phone || "",
          address: currentProfile.address || "",
          password: "",
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load employee dashboard.");
    } finally {
      setLoading(false);
    }
  }, [UId, branch_id, isReviewer, loadAttendance]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!tabs.includes(activeTab)) {
      setActiveTab(tabs[0]);
    }
  }, [activeTab, tabs]);

  const resetDutyForm = () => {
    setDutyEditId(null);
    setDutyForm(EMPTY_DUTY_FORM);
  };

  const resetEntryForm = () => {
    setEntryEditId(null);
    setEntryForm(EMPTY_ENTRY_FORM);
  };

  const resetDocumentForm = () => {
    setDocumentEditId(null);
    setDocumentFile(null);
    setDocumentForm(EMPTY_DOCUMENT_FORM);
  };

  const beginDutyEdit = (row) => {
    setActiveTab("duty");
    setDutyEditId(row.id);
    setDutyForm({
      staff_timing_id: row.staff_timing_id ? String(row.staff_timing_id) : "",
      day: row.day || "",
      requested_start_time: row.requested_start_time || row.current_start_time || "",
      requested_end_time: row.requested_end_time || row.current_end_time || "",
      reason: row.reason || "",
    });
  };

  const beginEntryEdit = (row) => {
    setActiveTab("background");
    setEntryEditId(row.id);
    setEntryForm({
      entry_type: row.entry_type || "Qualification",
      title: row.title || "",
      organization: row.organization || "",
      location: row.location || "",
      start_date: row.start_date || "",
      end_date: row.end_date || "",
      description: row.description || "",
    });
  };

  const beginDocumentEdit = (row) => {
    if (!canEmployeeEditDocument(row)) {
      toast.info("Approved documents are locked and can only be viewed.");
      return;
    }

    setActiveTab("documents");
    setDocumentEditId(row.id);
    setDocumentFile(null);
    setDocumentForm({
      document_category: row.document_category || "CNIC",
      document_type: row.document_type || "Document",
      issue_date: row.issue_date || TODAY_STRING,
      subject: row.subject || "",
      description: row.description || "",
      document_code: row.document_code || "",
    });
  };

  const submitProfile = async (event) => {
    event.preventDefault();
    setSavingKey("profile");

    try {
      const data = new FormData();
      Object.entries(profileForm).forEach(([key, value]) => {
        if (hasValue(value)) data.append(key, value);
      });

      if (profileFile) {
        data.append("file", profileFile);
        data.append("image_upload_from", "gallery");
      }

      await UserServices.UpdateStaffProfile(data, UId);
      toast.success("Profile updated successfully.");
      setProfileFile(null);
      await loadDashboard();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update profile.");
    } finally {
      setSavingKey("");
    }
  };

  const submitDutyRequest = async (event) => {
    event.preventDefault();

    if (!activeBranchId) {
      toast.error("Branch is not assigned on this employee profile.");
      return;
    }

    if (!dutyForm.day || !dutyForm.requested_start_time || !dutyForm.requested_end_time) {
      toast.error("Please complete the duty-hour request form.");
      return;
    }

    setSavingKey("duty");

    try {
      if (dutyEditId) {
        await UserServices.updateEmployeeDutyHourRequest(dutyEditId, dutyForm);
        toast.success("Duty-hour request updated.");
      } else {
        await UserServices.addEmployeeDutyHourRequest({
          ...dutyForm,
          branch_id: activeBranchId,
          user_id: UId,
        });
        toast.success("Duty-hour request submitted.");
      }

      resetDutyForm();
      await loadDashboard();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to submit duty-hour request.");
    } finally {
      setSavingKey("");
    }
  };

  const submitLeave = async (event) => {
    event.preventDefault();

    if (!activeBranchId) {
      toast.error("Branch is not assigned on this employee profile.");
      return;
    }

    if (!leaveForm.from || !leaveForm.to) {
      toast.error("Please select both leave dates.");
      return;
    }

    const fromDate = new Date(leaveForm.from);
    const toDate = new Date(leaveForm.to);

    if (toDate < fromDate) {
      toast.error("Leave end date must be after the start date.");
      return;
    }

    const days = Math.max(1, Math.round((toDate - fromDate) / 86400000) + 1);
    setSavingKey("leave");

    try {
      const payload = {
        ...leaveForm,
        branch_id: activeBranchId,
        user_id: UId,
      };

      await UserServices.existingLeaveCheck(days, payload);
      await UserServices.eligibilityLeaveCheck(payload);
      await UserServices.availabilityLeaveCheck(days, payload);
      await UserServices.addLeaveApplication(payload, days);
      toast.success("Leave application submitted.");
      setLeaveForm(EMPTY_LEAVE_FORM);
      await loadDashboard();
    } catch (error) {
      toast.error(
        error?.response?.data?.messages ||
          error?.response?.data?.message ||
          "Unable to submit leave application."
      );
    } finally {
      setSavingKey("");
    }
  };

  const submitEntry = async (event) => {
    event.preventDefault();

    if (!activeBranchId) {
      toast.error("Branch is not assigned on this employee profile.");
      return;
    }

    setSavingKey("entry");

    try {
      if (entryEditId) {
        await UserServices.updateEmployeeProfileEntry(entryEditId, entryForm);
        toast.success("Record updated successfully.");
      } else {
        await UserServices.addEmployeeProfileEntry({
          ...entryForm,
          branch_id: activeBranchId,
          user_id: UId,
        });
        toast.success("Record added successfully.");
      }

      resetEntryForm();
      await loadDashboard();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save record.");
    } finally {
      setSavingKey("");
    }
  };

  const archiveEntry = async (id) => {
    setSavingKey(`entry-${id}`);

    try {
      await UserServices.employeeProfileEntryActions(id, "0");
      toast.success("Record removed from your profile.");
      await loadDashboard();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update record.");
    } finally {
      setSavingKey("");
    }
  };

  const submitDocument = async (event) => {
    event.preventDefault();

    if (!activeBranchId) {
      toast.error("Branch is not assigned on this employee profile.");
      return;
    }

    if (!documentEditId && !documentFile) {
      toast.error("Please upload a document file.");
      return;
    }

    if (documentEditId) {
      const currentDocument = documents.find((row) => row.id === documentEditId);
      if (currentDocument && !canEmployeeEditDocument(currentDocument)) {
        toast.error("Approved documents can only be viewed.");
        return;
      }
    }

    setSavingKey("document");

    try {
      if (documentFile) {
        const allowedTypes = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "application/pdf",
        ];
        const maxSizeInBytes = 5 * 1024 * 1024;

        if (!allowedTypes.includes(documentFile.type)) {
          toast.error("Only PDF, JPG, JPEG, and PNG files are allowed.");
          return;
        }

        if (documentFile.size > maxSizeInBytes) {
          toast.error("File size must be 5 MB or less.");
          return;
        }
      }

      const data = new FormData();
      Object.entries({
        ...documentForm,
        branch_id: activeBranchId,
        user_id: UId,
      }).forEach(([key, value]) => {
        data.append(key, value || "");
      });

      if (documentFile) {
        data.append("document_file", documentFile);
      }

      if (documentEditId) {
        await UserServices.updateEmployeeDocument(documentEditId, data);
        toast.success("Document updated and sent for review.");
      } else {
        await UserServices.addEmployeeDocument(data);
        toast.success("Document uploaded successfully.");
      }

      resetDocumentForm();
      await loadDashboard();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save document.");
    } finally {
      setSavingKey("");
    }
  };

  const reviewRecord = async (type, id, approvalStatus) => {
    const noteKey = `${type}-${id}`;
    setSavingKey(`review-${noteKey}-${approvalStatus}`);

    try {
      const payload = {
        approval_status: approvalStatus,
        review_notes: reviewNotes[noteKey] || "",
      };

      if (type === "duty") {
        await UserServices.reviewEmployeeDutyHourRequest(id, payload);
      } else {
        await UserServices.reviewEmployeeDocument(id, payload);
      }

      setReviewNotes((previous) => ({
        ...previous,
        [noteKey]: "",
      }));
      toast.success(`Request ${approvalStatus.toLowerCase()} successfully.`);
      await loadDashboard();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to review request.");
    } finally {
      setSavingKey("");
    }
  };

  const leaveDaysRequested =
    leaveForm.from && leaveForm.to
      ? Math.max(
          0,
          Math.round((new Date(leaveForm.to) - new Date(leaveForm.from)) / 86400000) + 1
        )
      : 0;

  const submitAttendanceFilters = async (event) => {
    event.preventDefault();

    if (!activeBranchId) {
      toast.error("Branch is not assigned on this employee profile.");
      return;
    }

    if (!attendanceFilters.start_date || !attendanceFilters.end_date) {
      toast.error("Please select both attendance dates.");
      return;
    }

    if (new Date(attendanceFilters.end_date) < new Date(attendanceFilters.start_date)) {
      toast.error("End date must be after the start date.");
      return;
    }

    setSavingKey("attendance");

    try {
      await loadAttendance(activeBranchId, attendanceFilters);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load attendance.");
    } finally {
      setSavingKey("");
    }
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card employee-dashboard-card">
            <div className="card-body employee-dashboard-shell">
              {loading ? (
                <div className="employee-loading-state">Loading employee dashboard...</div>
              ) : (
                <>
                  <header className="employee-hero">
                    <div className="employee-hero-main">
                      <img
                        src={profile?.image || defaultImage}
                        alt="Employee profile"
                        className="employee-avatar"
                      />
                      <div className="employee-hero-copy">
                        <span className="employee-eyebrow">Employee Dashboard</span>
                        <h2>{getDisplayName(profile)}</h2>
                        <p>
                          A simple space for profile updates, leave requests, salary details,
                          documents, and HR approvals.
                        </p>
                        <div className="employee-chip-row">
                          <span className="employee-chip">{ROLE_LABELS[role] || "Employee"}</span>
                          <span className="employee-chip">
                            {profile?.department || profile?.department_name || "Department not assigned"}
                          </span>
                          <span className="employee-chip">
                            {profile?.designation || profile?.designation_name || "Designation not assigned"}
                          </span>
                          <span className="employee-chip">
                            {profile?.branch_name || profile?.branch || "Branch not assigned"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="employee-hero-actions">
                      <Link className="btn btn-primary" to={`/salary-slip/${UId}`}>
                        Download Salary Slip
                      </Link>
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setActiveTab("profile")}
                      >
                        Update Contact Details
                      </button>
                      <div className="employee-hero-note">
                        <span>Today: {formatDate(TODAY_STRING)}</span>
                        <span>Check In: {formatAttendanceClock(todayAttendance?.checkin_time_24h || todayAttendance?.checkin_time_12h)}</span>
                        <span>Check Out: {formatAttendanceClock(todayAttendance?.checkout_time_24h || todayAttendance?.checkout_time_12h)}</span>
                        <span>Status: {getAttendanceStatus(todayAttendance)}</span>
                      </div>
                      <div className="employee-hero-note">
                        <span>Employee ID: {profile?.uid || "N/A"}</span>
                        <span>Joining: {formatDate(profile?.joining || profile?.joining_date)}</span>
                      </div>
                    </div>
                  </header>

                  {announcements.length ? (
                    <section className="employee-announcement-board">
                      <div className="employee-announcement-header">
                        <div className="employee-announcement-badge">
                          <i className="bi bi-megaphone-fill"></i>
                          <span>Live Announcements</span>
                        </div>
                      </div>
                      <div className="employee-announcement-list">
                        {announcements.map((row) => (
                          <article className="employee-announcement-card" key={row.id}>
                            <div className="employee-announcement-top">
                              <span className={announcementPriorityClass(row.priority)}>
                                {row.priority} Priority
                              </span>
                              <span className="employee-announcement-expiry">{formatDateTime(row.visible_until)}</span>
                            </div>
                            <h4>{row.title}</h4>
                            <div className="employee-announcement-meta">
                              <span>Announced by: {row.announced_by || "Management"}</span>
                              <span>Starts: {formatDateTime(row.visible_from)}</span>
                            </div>
                            <p>{row.description}</p>
                          </article>
                        ))}
                      </div>
                    </section>
                  ) : null}

                  <section className="employee-metrics-grid">
                    {summaryCards.map((card) => (
                      <SummaryCard
                        key={card.label}
                        label={card.label}
                        value={card.value}
                        meta={card.meta}
                      />
                    ))}
                  </section>

                  <div className="employee-tabs">
                    {tabs.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`employee-tab ${activeTab === tab ? "active" : ""}`}
                        onClick={() => setActiveTab(tab)}
                      >
                        {tab === "background"
                          ? "Qualifications & Experience"
                          : tab === "attendance"
                          ? "Attendance"
                          : tab === "duty"
                          ? "Duty Hours"
                          : tab}
                      </button>
                    ))}
                  </div>

                  {activeTab === "profile" ? (
                    <section className="employee-section-stack">
                      <div className="employee-grid-two">
                        <div className="employee-panel-stack">
                          <div className="employee-panel">
                            <SectionTitle
                              eyebrow="Profile"
                              title="Personal Information"
                              description="This includes the main employee details created by HR."
                            />
                            <DetailGrid items={profileSections.personal} />
                          </div>
                          <div className="employee-panel">
                            <SectionTitle
                              eyebrow="Employment"
                              title="Job Information"
                              description="Core employment information currently assigned to your profile."
                            />
                            <DetailGrid items={profileSections.employment} />
                          </div>
                        </div>

                        <div className="employee-panel-stack">
                          <div className="employee-panel">
                            <SectionTitle
                              eyebrow="Editable"
                              title="Update Contact Details"
                              description="You can update CNIC, personal email, password, phone number, address, and profile picture here. Official email is managed by HR."
                            />
                            <form className="employee-form-grid" onSubmit={submitProfile}>
                              <input
                                className="form-control"
                                placeholder="CNIC"
                                value={profileForm.cnic}
                                onChange={(event) =>
                                  setProfileForm({ ...profileForm, cnic: event.target.value })
                                }
                              />
                              <input
                                className="form-control"
                                placeholder="Email"
                                value={profileForm.email}
                                onChange={(event) =>
                                  setProfileForm({ ...profileForm, email: event.target.value })
                                }
                              />
                              <input
                                className="form-control"
                                placeholder="Phone Number"
                                value={profileForm.phone}
                                onChange={(event) =>
                                  setProfileForm({ ...profileForm, phone: event.target.value })
                                }
                              />
                              <input
                                className="form-control"
                                type="password"
                                placeholder="New Password"
                                value={profileForm.password}
                                onChange={(event) =>
                                  setProfileForm({ ...profileForm, password: event.target.value })
                                }
                              />
                              <textarea
                                className="form-control full"
                                rows="4"
                                placeholder="Address"
                                value={profileForm.address}
                                onChange={(event) =>
                                  setProfileForm({ ...profileForm, address: event.target.value })
                                }
                              />
                              <div className="full">
                                <input
                                  className="form-control"
                                  type="file"
                                  onChange={(event) =>
                                    setProfileFile(event.target.files?.[0] || null)
                                  }
                                />
                                <div className="employee-form-hint">
                                  Upload a new profile picture if you want to replace the current one.
                                </div>
                              </div>
                              <button className="btn btn-primary full" disabled={savingKey === "profile"}>
                                {savingKey === "profile" ? "Saving..." : "Save Profile Updates"}
                              </button>
                            </form>
                          </div>

                          <div className="employee-panel">
                            <SectionTitle
                              eyebrow="Reference"
                              title="Additional HR Details"
                              description="Any extra information saved by HR will appear here so the profile stays complete."
                            />
                            {profileSections.additional.length ? (
                              <DetailGrid items={profileSections.additional} />
                            ) : (
                              <DetailGrid items={profileSections.editable} />
                            )}
                          </div>
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "attendance" ? (
                    <section className="employee-section-stack">
                      <div className="employee-metrics-grid employee-metrics-grid-compact">
                        <SummaryCard
                          label="Attendance Records"
                          value={`${attendanceSummary.total}`}
                          meta="Within selected date range"
                        />
                        <SummaryCard
                          label="Present"
                          value={`${attendanceSummary.present}`}
                          meta="Marked present"
                        />
                        <SummaryCard
                          label="Late"
                          value={`${attendanceSummary.late}`}
                          meta="Late arrivals"
                        />
                        <SummaryCard
                          label="Absent"
                          value={`${attendanceSummary.absent}`}
                          meta="Marked absent"
                        />
                        <SummaryCard
                          label="Today Status"
                          value={getAttendanceStatus(todayAttendance)}
                          meta={`Check in ${formatAttendanceClock(
                            todayAttendance?.checkin_time_24h || todayAttendance?.checkin_time_12h
                          )}`}
                        />
                      </div>

                      <div className="employee-panel">
                        <SectionTitle
                          eyebrow="Attendance"
                          title="Attendance History"
                          description="Filter by start and end date to review your detailed attendance history."
                        />
                        <form className="employee-form-grid" onSubmit={submitAttendanceFilters}>
                          <input
                            className="form-control"
                            type="date"
                            value={attendanceFilters.start_date}
                            onChange={(event) =>
                              setAttendanceFilters({
                                ...attendanceFilters,
                                start_date: event.target.value,
                              })
                            }
                          />
                          <input
                            className="form-control"
                            type="date"
                            value={attendanceFilters.end_date}
                            onChange={(event) =>
                              setAttendanceFilters({
                                ...attendanceFilters,
                                end_date: event.target.value,
                              })
                            }
                          />
                          <div className="employee-form-actions">
                            <button className="btn btn-primary" disabled={savingKey === "attendance"}>
                              {savingKey === "attendance" ? "Loading..." : "Load Attendance"}
                            </button>
                          </div>
                        </form>

                        <div className="employee-subsection">
                          {attendanceRows.length ? (
                            <div className="employee-attendance-table-wrap">
                              <table className="table table-hover align-middle mb-0">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th>Duty Hours</th>
                                    <th>Check In</th>
                                    <th>Check Out</th>
                                    <th>Working Hours</th>
                                    <th>Remarks</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {attendanceRows.map((row, index) => (
                                    <tr key={row.id || `${row.date}-${index}`}>
                                      <td>{formatDate(row.date)}</td>
                                      <td>
                                        <span className={badgeClass(getAttendanceStatus(row))}>
                                          {getAttendanceStatus(row)}
                                        </span>
                                      </td>
                                      <td>{getAttendanceDutyHours(row)}</td>
                                      <td>
                                        {formatAttendanceClock(
                                          row.checkin_time_24h || row.checkin_time_12h
                                        )}
                                      </td>
                                      <td>
                                        {formatAttendanceClock(
                                          row.checkout_time_24h || row.checkout_time_12h
                                        )}
                                      </td>
                                      <td>{row.working_hours || "N/A"}</td>
                                      <td>{row.remarks || row.reason || "N/A"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <EmptyState
                              title="No attendance records found"
                              text="Select a date range and load your attendance to view the details here."
                            />
                          )}
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "duty" ? (
                    <section className="employee-section-stack">
                      <div className="employee-grid-two">
                        <div className="employee-panel-stack">
                          <div className="employee-panel">
                            <SectionTitle
                              eyebrow="Current Schedule"
                              title="Assigned Duty Hours"
                              description="These are your currently active duty-hour slots."
                            />
                            {dutyHours.length ? (
                              <div className="employee-card-list">
                                {dutyHours.map((row) => (
                                  <div className="employee-list-card" key={row.id}>
                                    <div>
                                      <strong className="employee-list-title">{row.day}</strong>
                                      <div className="employee-list-meta">
                                        {formatTimeRange(row.start_time, row.end_time)}
                                      </div>
                                    </div>
                                    <span className="status-badge approved">Active</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <EmptyState
                                title="No duty hours found"
                                text="Your active schedule will appear here once HR assigns it."
                              />
                            )}
                          </div>

                          <div className="employee-panel">
                            <SectionTitle
                              eyebrow="Request History"
                              title="Duty-Hour Requests"
                              description="Pending requests can be edited until HR reviews them."
                            />
                            {dutyRequests.length ? (
                              <div className="employee-card-list">
                                {dutyRequests.map((row) => (
                                  <div className="employee-list-card" key={row.id}>
                                    <div>
                                      <strong className="employee-list-title">{row.day}</strong>
                                      <div className="employee-list-meta">
                                        Current: {formatTimeRange(row.current_start_time, row.current_end_time)}
                                      </div>
                                      <div className="employee-list-meta">
                                        Requested: {formatTimeRange(row.requested_start_time, row.requested_end_time)}
                                      </div>
                                      {row.reason ? (
                                        <div className="employee-list-note">Reason: {row.reason}</div>
                                      ) : null}
                                      {row.review_notes ? (
                                        <div className="employee-list-note">
                                          HR Notes: {row.review_notes}
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className="employee-list-actions">
                                      <span className={badgeClass(row.approval_status)}>
                                        {row.approval_status || "Pending"}
                                      </span>
                                      {row.approval_status === "Pending" ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() => beginDutyEdit(row)}
                                        >
                                          Edit Request
                                        </button>
                                      ) : null}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <EmptyState
                                title="No duty-hour requests yet"
                                text="Use the request form to ask HR for a timing change."
                              />
                            )}
                          </div>
                        </div>

                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="Approval Flow"
                            title={dutyEditId ? "Update Pending Request" : "Request Duty-Hour Change"}
                            description="Requested timing changes are sent to HR and only apply after approval."
                          />
                          {dutyEditId ? (
                            <div className="employee-warning-box">
                              You are editing a pending request. Save the changes to update the request,
                              or cancel to create a new one later.
                            </div>
                          ) : null}
                          <form className="employee-form-grid" onSubmit={submitDutyRequest}>
                            <select
                              className="form-select full"
                              value={dutyForm.staff_timing_id}
                              onChange={(event) => {
                                const selectedId = event.target.value;
                                const picked = dutyHours.find((row) => String(row.id) === selectedId);

                                setDutyForm({
                                  ...dutyForm,
                                  staff_timing_id: selectedId,
                                  day: picked?.day || dutyForm.day,
                                  requested_start_time: picked?.start_time || dutyForm.requested_start_time,
                                  requested_end_time: picked?.end_time || dutyForm.requested_end_time,
                                });
                              }}
                            >
                              <option value="">Select current slot</option>
                              {dutyHours.map((row) => (
                                <option key={row.id} value={row.id}>
                                  {row.day}: {formatTimeRange(row.start_time, row.end_time)}
                                </option>
                              ))}
                            </select>
                            <input
                              className="form-control"
                              placeholder="Day"
                              value={dutyForm.day}
                              onChange={(event) =>
                                setDutyForm({ ...dutyForm, day: event.target.value })
                              }
                            />
                            <input
                              className="form-control"
                              type="time"
                              value={dutyForm.requested_start_time}
                              onChange={(event) =>
                                setDutyForm({
                                  ...dutyForm,
                                  requested_start_time: event.target.value,
                                })
                              }
                            />
                            <input
                              className="form-control"
                              type="time"
                              value={dutyForm.requested_end_time}
                              onChange={(event) =>
                                setDutyForm({
                                  ...dutyForm,
                                  requested_end_time: event.target.value,
                                })
                              }
                            />
                            <textarea
                              className="form-control full"
                              rows="4"
                              placeholder="Reason for requested change"
                              value={dutyForm.reason}
                              onChange={(event) =>
                                setDutyForm({ ...dutyForm, reason: event.target.value })
                              }
                            />
                            <div className="employee-form-actions">
                              <button className="btn btn-primary" disabled={savingKey === "duty"}>
                                {savingKey === "duty"
                                  ? "Saving..."
                                  : dutyEditId
                                  ? "Update Request"
                                  : "Submit Request"}
                              </button>
                              {dutyEditId ? (
                                <button
                                  type="button"
                                  className="btn btn-light"
                                  onClick={resetDutyForm}
                                >
                                  Cancel Edit
                                </button>
                              ) : null}
                            </div>
                          </form>
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "salary" ? (
                    <section className="employee-section-stack">
                      <div className="employee-metrics-grid employee-metrics-grid-compact">
                        <SummaryCard
                          label="Base Salary"
                          value={formatCurrency(latestSalary.salary || profile?.salary || 0)}
                          meta="Current salary"
                        />
                        <SummaryCard
                          label="Deductions"
                          value={formatCurrency(deductionAmount)}
                          meta="Latest deductions"
                        />
                        <SummaryCard
                          label="Medical"
                          value={formatCurrency(latestSalary.medical || 0)}
                          meta="Medical allowance"
                        />
                      </div>

                      <div className="employee-grid-two">
                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="Payroll"
                            title="Salary Breakdown"
                            description="A quick snapshot of the most recent salary figures available in the system."
                          />
                          <DetailGrid
                            items={[
                              { label: "Base Salary", value: formatCurrency(latestSalary.salary || profile?.salary || 0) },
                              { label: "Reward", value: formatCurrency(latestSalary.reward || 0) },
                              { label: "Advance", value: formatCurrency(latestSalary.advance || 0) },
                              { label: "Fine", value: formatCurrency(latestSalary.fine || 0) },
                              { label: "Loan", value: formatCurrency(latestSalary.loan || 0) },
                              { label: "Net Salary", value: formatCurrency(latestSalary.net_salary || 0) },
                            ]}
                          />
                          <div className="employee-subsection">
                            <Link className="btn btn-primary" to={`/salary-slip/${UId}`}>
                              Open Salary Slip
                            </Link>
                          </div>
                        </div>

                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="Career Growth"
                            title="Promotions and Salary Changes"
                            description="Recent promotion history and salary changes are shown here."
                          />
                          {promotions.length ? (
                            <div className="employee-card-list">
                              {promotions.slice(0, 8).map((row) => (
                                <div className="employee-list-card" key={row.id}>
                                  <div>
                                    <strong className="employee-list-title">
                                      {row.promotion_type || row.type || "Promotion"}
                                    </strong>
                                    <div className="employee-list-meta">
                                      Effective: {formatDate(row.date || row.created_at)}
                                    </div>
                                    <div className="employee-list-meta">
                                      New Salary: {formatCurrency(row.new_salary || 0)}
                                    </div>
                                  </div>
                                  <span className="status-badge approved">Recorded</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <EmptyState
                              title="No promotions found"
                              text="Promotion and salary-change history will show up here when available."
                            />
                          )}
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "leave" ? (
                    <section className="employee-section-stack">
                      <div className="employee-grid-two">
                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="Leave Summary"
                            title="Quota and Recent Applications"
                            description="Track remaining quota and see the status of your recent leave applications."
                          />
                          {leaveQuota.length ? (
                            <div className="employee-card-list">
                              {leaveQuota.map((row) => {
                                const allowed = Number(row.number_of_leaves || 0);
                                const taken = Number(row.leaves_taken || 0);
                                const remaining = Math.max(allowed - taken, 0);

                                return (
                                  <div className="employee-list-card" key={row.id}>
                                    <div>
                                      <strong className="employee-list-title">
                                        {row.leave_type || "Leave"}
                                      </strong>
                                      <div className="employee-list-meta">
                                        Taken: {taken} of {allowed}
                                      </div>
                                    </div>
                                    <span className="status-badge approved">{remaining} Left</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <EmptyState
                              title="No leave quota assigned"
                              text="HR-assigned leave quota will appear here once it is available."
                            />
                          )}

                          <div className="employee-subsection">
                            <SectionTitle
                              title="Application History"
                              description="Recent leave applications with their current approval status."
                            />
                            {leaveApplications.length ? (
                              <div className="employee-card-list">
                                {leaveApplications.map((row) => (
                                  <div className="employee-list-card" key={row.id}>
                                    <div>
                                      <strong className="employee-list-title">
                                        {row.leave_type || row.type || "Leave"}
                                      </strong>
                                      <div className="employee-list-meta">
                                        {formatDate(row.from)} to {formatDate(row.to)}
                                      </div>
                                      {row.reason ? (
                                        <div className="employee-list-note">Reason: {row.reason}</div>
                                      ) : null}
                                    </div>
                                    <span className={badgeClass(row.application_status || "Pending")}>
                                      {row.application_status || "Pending"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <EmptyState
                                title="No leave applications yet"
                                text="Apply for leave from the form on the right side."
                              />
                            )}
                          </div>
                        </div>

                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="Apply"
                            title="Request Leave"
                            description="Submit a leave request and the system will validate quota and eligibility before saving it."
                          />
                          <form className="employee-form-grid" onSubmit={submitLeave}>
                            <select
                              className="form-select"
                              value={leaveForm.type}
                              onChange={(event) =>
                                setLeaveForm({ ...leaveForm, type: event.target.value })
                              }
                            >
                              <option value="Annual">Annual</option>
                              <option value="Casual">Casual</option>
                              <option value="Medical">Medical</option>
                            </select>
                            <select
                              className="form-select"
                              value={leaveForm.category}
                              onChange={(event) =>
                                setLeaveForm({ ...leaveForm, category: event.target.value })
                              }
                            >
                              <option value="Full">Full Day</option>
                            </select>
                            <input
                              className="form-control"
                              type="date"
                              value={leaveForm.from}
                              onChange={(event) =>
                                setLeaveForm({ ...leaveForm, from: event.target.value })
                              }
                            />
                            <input
                              className="form-control"
                              type="date"
                              value={leaveForm.to}
                              onChange={(event) =>
                                setLeaveForm({ ...leaveForm, to: event.target.value })
                              }
                            />
                            <textarea
                              className="form-control full"
                              rows="4"
                              placeholder="Reason for leave"
                              value={leaveForm.reason}
                              onChange={(event) =>
                                setLeaveForm({ ...leaveForm, reason: event.target.value })
                              }
                            />
                            <div className="employee-form-hint full">
                              {leaveDaysRequested > 0
                                ? `${leaveDaysRequested} day(s) selected.`
                                : "Select a start and end date to calculate leave days."}
                            </div>
                            <button className="btn btn-primary full" disabled={savingKey === "leave"}>
                              {savingKey === "leave" ? "Submitting..." : "Submit Leave Request"}
                            </button>
                          </form>
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "background" ? (
                    <section className="employee-section-stack">
                      <div className="employee-grid-two">
                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="Profile Building"
                            title={entryEditId ? "Update Record" : "Add Qualification or Experience"}
                            description="Add qualifications, work experience, and education details to keep your employee profile complete."
                          />
                          <form className="employee-form-grid" onSubmit={submitEntry}>
                            <select
                              className="form-select"
                              value={entryForm.entry_type}
                              onChange={(event) =>
                                setEntryForm({ ...entryForm, entry_type: event.target.value })
                              }
                            >
                              <option value="Qualification">Qualification</option>
                              <option value="Experience">Experience</option>
                              <option value="Education">Education</option>
                            </select>
                            <input
                              className="form-control"
                              placeholder="Title"
                              value={entryForm.title}
                              onChange={(event) =>
                                setEntryForm({ ...entryForm, title: event.target.value })
                              }
                            />
                            <input
                              className="form-control"
                              placeholder="Institute / Company"
                              value={entryForm.organization}
                              onChange={(event) =>
                                setEntryForm({ ...entryForm, organization: event.target.value })
                              }
                            />
                            <input
                              className="form-control"
                              placeholder="Location"
                              value={entryForm.location}
                              onChange={(event) =>
                                setEntryForm({ ...entryForm, location: event.target.value })
                              }
                            />
                            <input
                              className="form-control"
                              type="date"
                              value={entryForm.start_date}
                              onChange={(event) =>
                                setEntryForm({ ...entryForm, start_date: event.target.value })
                              }
                            />
                            <input
                              className="form-control"
                              type="date"
                              value={entryForm.end_date}
                              onChange={(event) =>
                                setEntryForm({ ...entryForm, end_date: event.target.value })
                              }
                            />
                            <textarea
                              className="form-control full"
                              rows="4"
                              placeholder="Description"
                              value={entryForm.description}
                              onChange={(event) =>
                                setEntryForm({ ...entryForm, description: event.target.value })
                              }
                            />
                            <div className="employee-form-actions">
                              <button className="btn btn-primary" disabled={savingKey === "entry"}>
                                {savingKey === "entry"
                                  ? "Saving..."
                                  : entryEditId
                                  ? "Update Record"
                                  : "Save Record"}
                              </button>
                              {entryEditId ? (
                                <button
                                  type="button"
                                  className="btn btn-light"
                                  onClick={resetEntryForm}
                                >
                                  Cancel Edit
                                </button>
                              ) : null}
                            </div>
                          </form>
                        </div>

                        <div className="employee-panel-stack">
                          {Object.entries(groupedEntries).map(([groupName, groupRows]) => (
                            <div className="employee-panel" key={groupName}>
                              <SectionTitle
                                eyebrow="Saved Records"
                                title={groupName}
                                description={`All saved ${groupName.toLowerCase()} details linked to your profile.`}
                              />
                              {groupRows.length ? (
                                <div className="employee-card-list">
                                  {groupRows.map((row) => (
                                    <div className="employee-list-card" key={row.id}>
                                      <div>
                                        <strong className="employee-list-title">{row.title}</strong>
                                        <div className="employee-list-meta">
                                          {row.organization || "N/A"}
                                          {row.location ? ` | ${row.location}` : ""}
                                        </div>
                                        <div className="employee-list-meta">
                                          {formatDate(row.start_date)} to {formatDate(row.end_date)}
                                        </div>
                                        {row.description ? (
                                          <div className="employee-list-note">{row.description}</div>
                                        ) : null}
                                      </div>
                                      <div className="employee-list-actions">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() => beginEntryEdit(row)}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          disabled={savingKey === `entry-${row.id}`}
                                          onClick={() => archiveEntry(row.id)}
                                        >
                                          {savingKey === `entry-${row.id}` ? "Removing..." : "Archive"}
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <EmptyState
                                  title={`No ${groupName.toLowerCase()} records yet`}
                                  text={`Use the form on the left to add ${groupName.toLowerCase()} details.`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "documents" ? (
                    <section className="employee-section-stack">
                      <div className="employee-grid-two">
                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="Verification"
                            title={documentEditId ? "Update Uploaded Document" : "Upload Document"}
                            description="Upload CNIC, CV, experience letters, or any other supporting document for HR review."
                          />
                          {documentEditId ? (
                            <div className="employee-warning-box">
                              Updating an existing document will send it back for HR review.
                            </div>
                          ) : null}
                          <form className="employee-form-grid" onSubmit={submitDocument}>
                            <select
                              className="form-select"
                              value={documentForm.document_category}
                              onChange={(event) =>
                                setDocumentForm({
                                  ...documentForm,
                                  document_category: event.target.value,
                                })
                              }
                            >
                              <option value="CNIC">CNIC</option>
                              <option value="CV">CV</option>
                              <option value="Experience Letter">Experience Letter</option>
                              <option value="Other">Other</option>
                            </select>
                            <input
                              className="form-control"
                              placeholder="Document Title"
                              value={documentForm.subject}
                              onChange={(event) =>
                                setDocumentForm({ ...documentForm, subject: event.target.value })
                              }
                            />
                              <input
                                className="form-control"
                                placeholder="Document Code (Optional)"
                                value={documentForm.document_code}
                                onChange={(event) =>
                                  setDocumentForm({
                                  ...documentForm,
                                  document_code: event.target.value,
                                })
                              }
                            />
                            <input
                              className="form-control"
                              type="date"
                              value={documentForm.issue_date}
                              onChange={(event) =>
                                setDocumentForm({ ...documentForm, issue_date: event.target.value })
                              }
                            />
                              <textarea
                                className="form-control full"
                                rows="4"
                                placeholder="Description (Optional)"
                                value={documentForm.description}
                                onChange={(event) =>
                                  setDocumentForm({
                                  ...documentForm,
                                  description: event.target.value,
                                })
                              }
                            />
                              <div className="full">
                                <input
                                  className="form-control"
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                  onChange={(event) =>
                                    setDocumentFile(event.target.files?.[0] || null)
                                  }
                                />
                                <div className="employee-form-hint">
                                  {documentEditId
                                    ? "Choose a PDF, JPG, JPEG, or PNG file up to 5 MB only if you want to replace the existing document."
                                    : "Uploaded documents stay pending until HR approves them. Allowed file types: PDF, JPG, JPEG, PNG. Max size: 5 MB."}
                                </div>
                              </div>
                            <div className="employee-form-actions">
                              <button className="btn btn-primary" disabled={savingKey === "document"}>
                                {savingKey === "document"
                                  ? "Saving..."
                                  : documentEditId
                                  ? "Update Document"
                                  : "Upload Document"}
                              </button>
                              {documentEditId ? (
                                <button
                                  type="button"
                                  className="btn btn-light"
                                  onClick={resetDocumentForm}
                                >
                                  Cancel Edit
                                </button>
                              ) : null}
                            </div>
                          </form>
                        </div>

                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="Document Library"
                            title="Uploaded and Verified Documents"
                            description="View status, review notes, and replace documents while they are still pending or rejected."
                          />
                          {documents.length ? (
                            <div className="employee-card-list">
                              {documents.map((row) => (
                                <div className="employee-list-card" key={row.id}>
                                  <div>
                                    <strong className="employee-list-title">
                                      {row.subject || row.document_category || "Document"}
                                    </strong>
                                    <div className="employee-list-meta">
                                      {row.document_category || "Document"} | {formatDate(row.issue_date)}
                                    </div>
                                    {row.original_file_name ? (
                                      <div className="employee-list-meta">
                                        File: {row.original_file_name}
                                      </div>
                                    ) : null}
                                    {row.description ? (
                                      <div className="employee-list-note">{row.description}</div>
                                    ) : null}
                                    {row.review_notes ? (
                                      <div className="employee-list-note">
                                        HR Notes: {row.review_notes}
                                      </div>
                                    ) : null}
                                    {row.file_url ? (
                                      <a
                                        href={row.file_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="employee-document-link"
                                      >
                                        View Document
                                      </a>
                                    ) : null}
                                  </div>
                                  <div className="employee-list-actions">
                                    <span className={badgeClass(row.approval_status || "Pending")}>
                                      {row.approval_status || "Pending"}
                                    </span>
                                    {canEmployeeEditDocument(row) ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => beginDocumentEdit(row)}
                                      >
                                        Edit / Replace
                                      </button>
                                    ) : (
                                      <span className="employee-form-hint">Locked after approval</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <EmptyState
                              title="No documents uploaded yet"
                              text="Upload your documents from the form on the left to start the approval flow."
                            />
                          )}
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "approvals" && isReviewer ? (
                    <section className="employee-section-stack">
                      <div className="employee-grid-two">
                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="HR Review"
                            title="Pending Duty-Hour Requests"
                            description="Review employee timing requests and approve or reject them with notes."
                          />
                          {pendingDutyApprovals.length ? (
                            <div className="employee-card-list">
                              {pendingDutyApprovals.map((row) => {
                                const noteKey = `duty-${row.id}`;

                                return (
                                  <div className="employee-list-card" key={row.id}>
                                    <div>
                                      <strong className="employee-list-title">
                                        {row.employee?.name || getDisplayName(row.employee) || "Employee"}
                                      </strong>
                                      <div className="employee-list-meta">{row.day}</div>
                                      <div className="employee-list-meta">
                                        Current: {formatTimeRange(row.current_start_time, row.current_end_time)}
                                      </div>
                                      <div className="employee-list-meta">
                                        Requested: {formatTimeRange(row.requested_start_time, row.requested_end_time)}
                                      </div>
                                      {row.reason ? (
                                        <div className="employee-list-note">Reason: {row.reason}</div>
                                      ) : null}
                                      <textarea
                                        className="form-control employee-review-note"
                                        rows="3"
                                        placeholder="Optional HR note"
                                        value={reviewNotes[noteKey] || ""}
                                        onChange={(event) =>
                                          setReviewNotes({
                                            ...reviewNotes,
                                            [noteKey]: event.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="employee-list-actions">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-success"
                                        disabled={savingKey === `review-${noteKey}-Approved`}
                                        onClick={() => reviewRecord("duty", row.id, "Approved")}
                                      >
                                        {savingKey === `review-${noteKey}-Approved`
                                          ? "Saving..."
                                          : "Approve"}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        disabled={savingKey === `review-${noteKey}-Rejected`}
                                        onClick={() => reviewRecord("duty", row.id, "Rejected")}
                                      >
                                        {savingKey === `review-${noteKey}-Rejected`
                                          ? "Saving..."
                                          : "Reject"}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <EmptyState
                              title="No pending duty-hour requests"
                              text="Once employees submit a duty-hour request, it will appear here for approval."
                            />
                          )}
                        </div>

                        <div className="employee-panel">
                          <SectionTitle
                            eyebrow="HR Review"
                            title="Pending Document Reviews"
                            description="Approve employee uploads after verifying the attached file and document details."
                          />
                          {pendingDocumentApprovals.length ? (
                            <div className="employee-card-list">
                              {pendingDocumentApprovals.map((row) => {
                                const noteKey = `document-${row.id}`;

                                return (
                                  <div className="employee-list-card" key={row.id}>
                                    <div>
                                      <strong className="employee-list-title">
                                        {row.employee?.name || getDisplayName(row.employee) || "Employee"}
                                      </strong>
                                      <div className="employee-list-meta">
                                        {row.subject || row.document_category || "Document"}
                                      </div>
                                      <div className="employee-list-meta">
                                        {row.document_category || "Document"} | {formatDate(row.issue_date)}
                                      </div>
                                      {row.file_url ? (
                                        <a
                                          href={row.file_url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="employee-document-link"
                                        >
                                          View Uploaded File
                                        </a>
                                      ) : null}
                                      <textarea
                                        className="form-control employee-review-note"
                                        rows="3"
                                        placeholder="Optional HR note"
                                        value={reviewNotes[noteKey] || ""}
                                        onChange={(event) =>
                                          setReviewNotes({
                                            ...reviewNotes,
                                            [noteKey]: event.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="employee-list-actions">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-success"
                                        disabled={savingKey === `review-${noteKey}-Approved`}
                                        onClick={() => reviewRecord("document", row.id, "Approved")}
                                      >
                                        {savingKey === `review-${noteKey}-Approved`
                                          ? "Saving..."
                                          : "Approve"}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        disabled={savingKey === `review-${noteKey}-Rejected`}
                                        onClick={() => reviewRecord("document", row.id, "Rejected")}
                                      >
                                        {savingKey === `review-${noteKey}-Rejected`
                                          ? "Saving..."
                                          : "Reject"}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <EmptyState
                              title="No pending document reviews"
                              text="New employee uploads will show up here until HR approves or rejects them."
                            />
                          )}
                        </div>
                      </div>
                    </section>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
